import {axiosClient} from '../axios';
import type {
  AreaType,
  StateType,
  WhiskyBarsIndexType,
  WhiskyBarType,
  DistilleryType,
  DistilleriesIndexType,
  WhiskyMapFeaturedTileType,
} from '../types';

import {
  convertFiltersToSearchString,
  MaybePaginatedGetter,
  FiltersOf,
  getPaginatedFiltersMapping,
  getViewKeyHeader,
  ViewKeyOptions,
} from '../utils';

export async function getStates(viewKey?: string) {
  const {data} = await axiosClient.get<ReadonlyArray<StateType>>('/states/', {
    headers: getViewKeyHeader(viewKey),
  });
  return data;
}

export async function getStateBySlug(slug: string, viewKey?: string) {
  const {data} = await axiosClient.get<StateType>(`/states/${slug}/`, {
    headers: getViewKeyHeader(viewKey),
  });
  return data;
}

export async function getWhiskyBarsIndex(viewKey?: string) {
  const {data} = await axiosClient.get<WhiskyBarsIndexType>(
    '/whisky-bar-index/',
    {headers: getViewKeyHeader(viewKey)},
  );
  return data;
}

interface AreasFilters {
  stateSlug?: string;
}

export async function getAreas(filters: AreasFilters, viewKey?: string) {
  const filterMapping = {stateSlug: 'state__slug'};
  const {data} = await axiosClient.get<ReadonlyArray<AreaType>>(
    `/areas/${convertFiltersToSearchString(filters, filterMapping)}`,
    {headers: getViewKeyHeader(viewKey)},
  );
  return data;
}

const whiskyBarsFiltersMapping = getPaginatedFiltersMapping({
  stateSlug: 'state__slug',
  area: 'area',
  areaSlug: 'area__slug',
  isTwlPartner: 'is_partner',
  order: 'ordering',
});

export const getWhiskyBars: MaybePaginatedGetter<
  WhiskyBarType,
  FiltersOf<typeof whiskyBarsFiltersMapping>
> = async (
  filters: FiltersOf<typeof whiskyBarsFiltersMapping>,
  options: ViewKeyOptions,
) => {
  const {data} = await axiosClient.get(
    `/whisky-bars/${convertFiltersToSearchString(
      filters,
      whiskyBarsFiltersMapping,
    )}`,
    {headers: getViewKeyHeader(options?.viewKey)},
  );
  return data;
};

export async function getWhiskyBarBySlug(slug: string, viewKey?: string) {
  const {data} = await axiosClient.get<WhiskyBarType>(`/whisky-bars/${slug}/`, {
    headers: getViewKeyHeader(viewKey),
  });
  return data;
}

const distilleriesFilterMapping = getPaginatedFiltersMapping({
  stateSlug: 'state__slug',
  spiritType: 'spirit_types__name',
  isTwlPartner: 'is_partner',
  order: 'ordering',
});

export const getDistilleries: MaybePaginatedGetter<
  DistilleryType,
  FiltersOf<typeof distilleriesFilterMapping>
> = async (
  filters: FiltersOf<typeof distilleriesFilterMapping>,
  options: ViewKeyOptions,
) => {
  const {data} = await axiosClient.get(
    `/whisky-distilleries/${convertFiltersToSearchString(
      filters,
      distilleriesFilterMapping,
    )}`,
    {headers: getViewKeyHeader(options?.viewKey)},
  );
  return data;
};

export async function getDistilleriesIndex(viewKey?: string) {
  const {data} = await axiosClient.get<DistilleriesIndexType>(
    '/distilleries-index/',
    {headers: getViewKeyHeader(viewKey)},
  );
  return data;
}

const spiritTypeFilterMapping = getPaginatedFiltersMapping({
  hasWhiskyDistilleriesInState: 'has_whisky_distilleries_in_state',
});

export const getSpiritTypes: MaybePaginatedGetter<
  DistilleryType,
  FiltersOf<typeof spiritTypeFilterMapping>
> = async (
  filters: FiltersOf<typeof spiritTypeFilterMapping>,
  options: ViewKeyOptions,
) => {
  const {data} = await axiosClient.get(
    `/spirit-types/${convertFiltersToSearchString(
      filters,
      spiritTypeFilterMapping,
    )}`,
    {headers: getViewKeyHeader(options?.viewKey)},
  );
  return data;
};

const whiskyMapFeaturedTileFilterMapping = getPaginatedFiltersMapping({});

export const getWhiskyMapFeaturedTile: MaybePaginatedGetter<
  WhiskyMapFeaturedTileType,
  FiltersOf<typeof whiskyMapFeaturedTileFilterMapping>
> = async (
  filters: FiltersOf<typeof whiskyMapFeaturedTileFilterMapping>,
  options: ViewKeyOptions,
) => {
  const {data} = await axiosClient.get(
    `/whisky-map-featured-tiles/${convertFiltersToSearchString(
      filters,
      whiskyMapFeaturedTileFilterMapping,
    )}`,
    {headers: getViewKeyHeader(options?.viewKey)},
  );
  return data;
};
